/**
 * Namespaced-tag utilities.
 *
 * Contact tags follow a `namespace:value` convention (`prio:0`, `seg:pharma`,
 * `event:medica-2026`, `market:de`, `src:linkedin`). A tag without a colon is
 * treated as a plain value with a null namespace.
 */

export interface ParsedTag {
  namespace: string | null;
  value: string;
}

/** Split a raw tag on the first `:` — no colon → namespace null. */
export function parseTag(raw: string): ParsedTag {
  const idx = raw.indexOf(":");
  if (idx === -1) return { namespace: null, value: raw };
  return { namespace: raw.slice(0, idx), value: raw.slice(idx + 1) };
}

/** First value for a namespace across a tag list, or null. */
export function tagValue(
  tags: string[] | null | undefined,
  namespace: string,
): string | null {
  for (const raw of tags || []) {
    const parsed = parseTag(raw);
    if (parsed.namespace === namespace) return parsed.value;
  }
  return null;
}

/**
 * Priority level from a `prio:0`..`prio:4` tag, or null when absent/malformed.
 * P0 is the highest priority.
 */
export function prioOf(tags: string[] | null | undefined): number | null {
  const raw = tagValue(tags, "prio");
  if (raw == null) return null;
  if (!/^[0-4]$/.test(raw)) return null;
  return Number(raw);
}
